import EventEmitter from 'eventemitter3';

import { ProtectedEventEmitter } from './utils/ProtectedEventEmitter';
import { promiseIgnoreCatch } from './utils/promiseIgnoreCatch';
import { Debugger } from './utils/Debugger';

import type { CrossOrigin } from './Def';

export enum ResourceType {
    JS = 'JS',
    CSS = 'CSS',
}

export enum ResourceState {
    NOT_LOADED = 'NOT_LOADED',
    LOADING = 'LOADING',
    UNLOADING = 'UNLOADING',
    LOADED = 'LOADED',
    LOAD_ERROR = 'LOAD_ERROR',
}

export interface ResourceEvent {
    statechange: { state: ResourceState };
    refcountchange: { refCount: number };
}

export abstract class Resource extends Debugger implements ProtectedEventEmitter<ResourceEvent> {
    #src: string;
    #type: ResourceType;
    #internalState: ResourceState = ResourceState.NOT_LOADED;

    #loadingPromise: Promise<void> | null = null;
    #unloadingPromise: Promise<void> | null = null;
    #error: Error | null = null;

    readonly #eventBus = new EventEmitter<keyof ResourceEvent>();

    readonly #refs: Map<unknown, 1> = new Map();
    #internalRefCount = 0;

    #crossOrigin: CrossOrigin;

    public off<T extends keyof ResourceEvent>(
        event: T,
        listener: (ev: ResourceEvent[T]) => unknown,
        context?: unknown
    ): this {
        this.#eventBus.off(event, listener, context);
        return this;
    }

    public on<T extends keyof ResourceEvent>(
        event: T,
        listener: (ev: ResourceEvent[T]) => unknown,
        context?: unknown
    ): this {
        this.#eventBus.on(event, listener, context);
        return this;
    }

    public once<T extends keyof ResourceEvent>(
        event: T,
        listener: (ev: ResourceEvent[T]) => unknown,
        context?: unknown
    ): this {
        this.#eventBus.once(event, listener, context);
        return this;
    }

    protected constructor(src: string, type: ResourceType, crossOrigin?: CrossOrigin) {
        super();
        this.#src = src;
        this.#type = type;
        this.#crossOrigin = crossOrigin;
    }

    public abstract get [Symbol.toStringTag](): string;

    protected get debugName(): string {
        return `resource:${this.type}:${this.src}`;
    }

    public override toString(): string {
        return `${this[Symbol.toStringTag]}(${this.#src})`;
    }

    protected get crossOrigin(): CrossOrigin {
        return this.#crossOrigin;
    }

    /**
     * Create a new resource by src and optional type.
     * @param src Source url of the resource.
     * @param type Type of resource, only CSS and JS are supported.
     * @returns Resource
     */
    public static create(src: string, type?: ResourceType, crossOrigin?: CrossOrigin): Resource {
        if (!type) {
            if (src.toLowerCase().endsWith('.js')) {
                type = ResourceType.JS;
            } else if (src.toLowerCase().endsWith('.css')) {
                type = ResourceType.CSS;
            } else {
                throw Error(`Unrecognized type of resource ${src}.`);
            }
        }

        switch (type) {
            case ResourceType.CSS:
                return new StyleResource(src, crossOrigin);
            case ResourceType.JS:
                return new ScriptResource(src, crossOrigin);
            default:
                throw Error(`Unknown type ${type} of resource.`);
        }
    }

    public get src(): string {
        return this.#src;
    }

    get #refCount(): number {
        return this.#internalRefCount;
    }

    set #refCount(refCount: number) {
        const oldRefCount = this.#internalRefCount;

        this.#internalRefCount = refCount;

        if (oldRefCount !== refCount) {
            this.debug('Set refCount to %d.', refCount);
            this.#emit('refcountchange', { refCount });
        }
    }

    public get refCount(): number {
        return this.#refCount;
    }

    public get state(): ResourceState {
        return this.#internalState;
    }

    public get type(): ResourceType {
        return this.#type;
    }

    public ref(obj: unknown): Promise<void> {
        if (!this.#refs.has(obj)) {
            this.#refs.set(obj, 1);
            this.#refCount += 1;
        }

        return this.load();
    }

    public deref(obj: unknown): Promise<void> {
        if (this.#refs.has(obj)) {
            this.#refs.delete(obj);
            this.#refCount = Math.max(0, this.#refCount - 1);
        }

        if (!this.#refCount) {
            return this.unload();
        }

        return Promise.resolve();
    }

    set #state(state: ResourceState) {
        const oldState = this.#internalState;
        this.#internalState = state;

        if (oldState !== state) {
            this.debug('Set state to %s.', state);
            this.#emit('statechange', { state });
        }
    }

    public abstract get element(): HTMLScriptElement | HTMLLinkElement | null;

    protected load(): Promise<void> {
        this.debug('Load() start.');

        if (this.#loadingPromise) {
            this.debug('Reuse existing #loadingPromise.');
            return this.#loadingPromise;
        }

        if (this.#unloadingPromise) {
            this.debug('Wait for the #unloadingPromise to finish up.');
            return promiseIgnoreCatch(this.#unloadingPromise).then(() => this.load());
        }

        switch (this.state) {
            case ResourceState.LOADED:
                this.debug('Load() is finished immediately cause state=%s can be treated as loaded.', this.state);
                return Promise.resolve();
            /* eslint-disable no-fallthrough */
            /* istanbul ignore next: never be this state */
            case ResourceState.LOADING:
            /* istanbul ignore next: never be this state */
            case ResourceState.UNLOADING:
                return Promise.reject(Error(`${this} never hits ${this.state} when loading.`));
            /* eslint-enable no-fallthrough */
            case ResourceState.LOAD_ERROR:
                this.debug(
                    `Load() is rejected immediately cause state=${this.state} can be treated as a load failure.`
                );
                return Promise.reject(this.#error ?? Error('Load error.'));
        }

        this.debug("Let's create #loadingPromise.");

        // state must be NOT_LOADED
        this.#state = ResourceState.LOADING;

        this.#loadingPromise = Promise.resolve()
            .then(() => this.fetchInBrowser())
            .then(
                () => {
                    this.debug('Load() complete.');
                    this.#state = ResourceState.LOADED;
                },
                err => {
                    this.debug('Load() failed with error: %O.', err);
                    this.#state = ResourceState.LOAD_ERROR;
                    this.#error = err.message ? err : Error(`${this} loading failed with unknown error.`);
                    throw this.#error;
                }
            )
            .finally(() => {
                this.#loadingPromise = null;
            });

        return this.#loadingPromise;
    }

    protected unload(): Promise<void> {
        if (this.#unloadingPromise) {
            this.debug('Reuse existing #unloadingPromise.');
            return this.#unloadingPromise;
        }

        if (this.#loadingPromise) {
            this.debug('Wait for the #loadingPromise to finish up.');
            return promiseIgnoreCatch(this.#loadingPromise).then(() => this.unload());
        }

        switch (this.state) {
            case ResourceState.NOT_LOADED:
                this.debug('Unload() is finished immediately cause state=%s can be treated as unloaded.', this.state);
                return Promise.resolve();
            /* eslint-disable no-fallthrough */
            /* istanbul ignore next: never be this state */
            case ResourceState.LOADING:
            /* istanbul ignore next: never be this state */
            case ResourceState.UNLOADING:
                this.debug('Should not hit %s.', this.state);
                return Promise.reject(Error(`${this} never hits ${this.state} when unloading.`));
            /* eslint-enable no-fallthrough */
            case ResourceState.LOAD_ERROR:
                this.debug('Unload() is finished immediately cause state=%s can be treated as unloaded.', this.state);
                return Promise.resolve();
        }

        this.debug("Let's create #unloadingPromise.");

        // state must be LOADED
        this.#state = ResourceState.UNLOADING;

        this.#unloadingPromise = Promise.resolve()
            .then(() => this.promiseUnload())
            .finally(() => {
                // ignore failure
                this.#state = ResourceState.NOT_LOADED;
                this.#unloadingPromise = null;
            });

        return this.#unloadingPromise;
    }

    protected abstract fetchInBrowser(): Promise<void>;
    protected abstract promiseUnload(): Promise<void>;

    #emit<T extends keyof ResourceEvent>(event: T, payload?: ResourceEvent[T]): void {
        this.debug('Emit %s with %O.', event, payload);
        // Should not break down.
        try {
            this.#eventBus.emit(event, payload);
        } catch {
            //
        }
    }
}

class ScriptResource extends Resource {
    #element: HTMLScriptElement | null = null;

    constructor(src: string, crossOrigin?: CrossOrigin) {
        super(src, ResourceType.JS, crossOrigin);
    }

    public override get element(): HTMLScriptElement | null {
        return this.#element;
    }

    public override get [Symbol.toStringTag](): string {
        return 'ScriptResource';
    }

    protected override fetchInBrowser(): Promise<void> {
        const loadingPromise: Promise<void> = new Promise((resolve, reject) => {
            const scriptNode: HTMLScriptElement = document.createElement('script');

            scriptNode.type = 'text/javascript';
            if (this.crossOrigin) {
                scriptNode.crossOrigin = this.crossOrigin;
            }

            const load = (): void => {
                removeListener();
                resolve();
            };

            const error = (ev: ErrorEvent): void => {
                removeListener();
                scriptNode.parentNode?.removeChild(scriptNode);
                reject(Error(ev.message));
            };

            const removeListener = (): void => {
                scriptNode.removeEventListener('load', load);
                scriptNode.removeEventListener('error', error);
            };

            scriptNode.addEventListener('load', load);
            scriptNode.addEventListener('error', error);
            scriptNode.src = this.src;
            this.#element = scriptNode;
            document.head.appendChild(scriptNode);
        });

        return loadingPromise;
    }

    protected override promiseUnload(): Promise<void> {
        return Promise.reject(Error(`${this} is ${this.type} type, which cannot be unloaded.`));
    }

    protected override unload(): Promise<void> {
        return this.promiseUnload();
    }
}

class StyleResource extends Resource {
    #element: HTMLLinkElement | null = null;

    constructor(src: string, crossOrigin?: CrossOrigin) {
        super(src, ResourceType.CSS, crossOrigin);
    }

    public override get element(): HTMLLinkElement | null {
        return this.#element;
    }

    public override get [Symbol.toStringTag](): string {
        return 'StyleResource';
    }

    protected override fetchInBrowser(): Promise<void> {
        const loadingPromise: Promise<void> = new Promise((resolve, reject) => {
            const linkNode: HTMLLinkElement = document.createElement('link');

            linkNode.rel = 'stylesheet';
            linkNode.type = 'text/css';

            if (this.crossOrigin) {
                linkNode.crossOrigin = this.crossOrigin;
            }

            const load = (): void => {
                removeListener();
                resolve();
            };

            const error = (ev: ErrorEvent): void => {
                removeListener();
                linkNode.parentNode?.removeChild(linkNode);
                reject(Error(ev.message));
            };

            const removeListener = (): void => {
                linkNode.removeEventListener('load', load);
                linkNode.removeEventListener('error', error);
            };

            linkNode.addEventListener('load', load);
            linkNode.addEventListener('error', error);
            linkNode.href = this.src;

            this.#element = linkNode;
            document.head.appendChild(linkNode);
        });

        return loadingPromise;
    }

    protected override promiseUnload(): Promise<void> {
        return Promise.resolve().then(() => {
            this.#element?.parentElement?.removeChild(this.#element);
            this.#element = null;
        });
    }
}
